/**
 * RAM — Change OD Protection (CFPB Reg E A-9 opt-in / opt-out).
 *
 * POST /v3/maint/acct/change-od-protection
 *
 * Reg E A-9 governs the customer's election for ATM and one-time debit-card
 * overdraft coverage on consumer transaction accounts. The opt-in election
 * is only valid with the customer's signature on the A-9 form; this UI
 * captures the election and an optional dollar amount (0 = decline).
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { emitDisclosureRedeliveryRequired } from '../disclosureTriggers';
import type { RamTarget } from '../types';

export interface ChangeOdProtectionProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type OdPrvlgOpt = 'opt-in' | 'opt-out' | 'pending';

export default function ChangeOdProtection({
  target,
  onDone,
  onCancel,
}: ChangeOdProtectionProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [odPrvlgOptVal, setOdPrvlgOptVal] = useState<OdPrvlgOpt | ''>('');
  const [odProtAmt, setOdProtAmt] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const numericAmt = Number.parseFloat(odProtAmt);
  const hasAmt = odProtAmt.trim() !== '' && !Number.isNaN(numericAmt) && numericAmt >= 0;
  const canSave = !submitting && Boolean(target.acctId) && Boolean(odPrvlgOptVal);

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId || !odPrvlgOptVal) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeOdProtection(b),
      {
        accountNumber: target.acctId,
        odPrvlgOptVal,
        odProtAmt: hasAmt ? numericAmt : undefined,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    // Reg E §1005.17 A-9 — every opt-in/opt-out election triggers redelivery.
    void emitDisclosureRedeliveryRequired(client, {
      action: 'change-od-protection',
      cifId: target.cifId,
      accountNumber: target.acctId,
      actorUpn: user?.upn,
      payload: { odPrvlgOptVal, odProtAmt: hasAmt ? numericAmt : null },
    });
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          OD protection change submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change OD protection</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Reg E A-9 opt-in/out applies to consumer transaction accounts only.
        Customer signature on the A-9 form is required to opt in.
      </Banner>

      <div className="ram-wizard__fields">
        <fieldset className="ram-search__label">
          <legend>Reg E A-9 election</legend>
          <label>
            <input
              type="radio"
              name="odPrvlgOptVal"
              value="opt-in"
              checked={odPrvlgOptVal === 'opt-in'}
              onChange={() => setOdPrvlgOptVal('opt-in')}
            />
            {' '}Opt in
          </label>
          <label>
            <input
              type="radio"
              name="odPrvlgOptVal"
              value="opt-out"
              checked={odPrvlgOptVal === 'opt-out'}
              onChange={() => setOdPrvlgOptVal('opt-out')}
            />
            {' '}Opt out
          </label>
          <label>
            <input
              type="radio"
              name="odPrvlgOptVal"
              value="pending"
              checked={odPrvlgOptVal === 'pending'}
              onChange={() => setOdPrvlgOptVal('pending')}
            />
            {' '}Pending
          </label>
        </fieldset>

        <label className="ram-search__label">
          OD protection amount ($) — 0 to decline
          <input
            className="ram-input"
            value={odProtAmt}
            onChange={(e) => setOdProtAmt(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder=""
          />
        </label>

        <label className="ram-search__label">
          Reason
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit OD protection change'}
        </Button>
      </div>
    </div>
  );
}
